import {useProduct} from "@montana/containers/product";
import {useTranslations} from "@montana/containers/translations";
import {isBrowser} from "@montana/helpers/browser.helper";
import {getDimensionTranslationValue} from "@montana/helpers/product.helper";
import useMarketSettings from "@montana/hooks/data/useMarketSettings";
import {IProductConfigOption} from "@montana/models/dev";
import {IColorModel, IOption, IProductDimension} from "@montana/models/generated/cms.interface";
import {Button, ButtonStyles} from "@montana/shared/button";
import {DynamicContentModal, IDynamicContentModalRef} from "@montana/shared/modal/dynamic-content-modal.component";
import {SmallerThanTabletLandscape, TabletLandscapeAndLarger} from "@montana/shared/responsive";
import {SvgIcon} from "@montana/shared/svg-icon";
import cx from "clsx";
import {darken, getContrast} from "color2k";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useMediaQuery} from "react-responsive";

export interface IDimensionPicker {
    dimension?: IProductDimension;
}

const COLOR_GRID_TRESHOLD = 40;
const mainColors = [
    "140-pomelo",
    "01-white",
    "101-newwhite",
    "38-snow",
    "150-vanilla",
    "156-oyster",
    "161-mist",
    "09-nordic",
    "158-oat",
    "131-whiteoak",
    "159-camomile",
    "148-flint",
    "02-fjord",
    "137-mushroom",
    "157-cumin",
    "154-azure",
    "144-fennel",
    "141-truffle",
    "100-graphic",
    "164-iris",
    "151-rhubarb",
    "142-amber",
    "135-monarch",
    "152-parsley",
    "35-coffee",
    "36-coal",
    "145-rosehip",
    "138-juniper",
    "136-pine",
    "139-oregano",
    "04-anthracite",
    "155-masala",
    "165-beetroot",
    "162-hokkaido",
    "147-shadow",
    "163-blackjade",
    "05-black",
    "133-blackoak",
    "160-balsamic",
    "146-hazelnut",
    "168-clay",
    "167-ruby",
    "166-acacia"
];

export type TabType = "color-grid" | "color-regular" | "text-label" | "pending";

type DimensionAndColor = IColorModel & IOption;

interface IDimensionTabType {
    type: TabType;
    key: string;
}

interface ITabMaterialProps {
    tabName: string;
    tabID: string;
    tabType: TabType;
    content: JSX.Element;
}

interface ITabContentProps {
    tabKey: string;
    tabType: TabType;
    dimension: IProductDimension;
    onHoverOption: (optionValue: string | undefined) => void;
    onSelectOption: (option: DimensionAndColor) => void;
    selectedDimension: IProductConfigOption | undefined;
}

interface IMaterialTileProps {
    hexcode: string;
    option: DimensionAndColor;
    onHoverOption: (optionValue: string | undefined) => void;
    onSelectOption: (option: DimensionAndColor) => void;
    isSelected: boolean;
    isColorGrid?: boolean;
}

interface IMaterialsTabListProps {
    tabsConfig: ITabMaterialProps[] | undefined;
    activeTab: ITabMaterialProps | undefined;
    onTabSelect: (tab: ITabMaterialProps) => void;
    selectedOption: string | undefined;
}

const MaterialsTabList: React.FC<IMaterialsTabListProps> = ({
                                                                tabsConfig,
                                                                activeTab,
                                                                onTabSelect,
                                                                selectedOption
                                                            }: IMaterialsTabListProps) => {
    return (
        <>
            {!!tabsConfig && (
                <>
                    <SmallerThanTabletLandscape>
                        {tabsConfig.map((tab, index) => {
                            const isActive = activeTab?.tabID === tab.tabID;

                            return (
                                <article
                                    className={cx("materials", {
                                        first: index === 0
                                    })}
                                    key={tab.tabID}
                                >
                                    {tab.tabID.toLowerCase() !== "default" && (
                                        <section className="material-tabs-wrapper">
                                            <div className="material-tabs">
                                                <span
                                                    className={cx(
                                                        "material-tabs__label",
                                                        {
                                                            "material-tabs__label--active": isActive
                                                        }
                                                    )}
                                                >
                                                    {tab.tabName}
                                                </span>
                                                {isActive && selectedOption}
                                            </div>
                                        </section>
                                    )}
                                    <section className="material-content-wrapper material-content-wrapper--mobile">
                                        {tab?.content}
                                    </section>
                                </article>
                            );
                        })}
                    </SmallerThanTabletLandscape>
                    <TabletLandscapeAndLarger>
                        <section
                            className={cx("material-tabs-wrapper", {
                                "material-tabs-wrapper--zero-size":
                                    tabsConfig.length <= 1 &&
                                    tabsConfig[0].tabID.toLowerCase() ===
                                    "default"
                            })}
                        >
                            <nav className="material-tabs" role="tablist">
                                {tabsConfig.map(tab => {
                                    const isActive =
                                        tab.tabID === activeTab?.tabID;

                                    return (
                                        tab.tabID.toLowerCase() !==
                                        "default" && (
                                            <Button
                                                key={tab.tabID}
                                                id={`${tab.tabID}-button`}
                                                onClick={() => onTabSelect(tab)}
                                                className={cx({
                                                    "material-tab--active":
                                                        tab.tabID ===
                                                        activeTab?.tabID
                                                })}
                                                text={tab.tabName}
                                                inverseStrokeAnimation
                                                role="tab"
                                                ariaControls={`${tab.tabID}-content`}
                                                ariaSelected={isActive.toString()}
                                                strokeIsActive={isActive}
                                                style={ButtonStyles.Text}
                                            />
                                        )
                                    );
                                })}
                            </nav>
                        </section>
                        <section
                            className="material-content-wrapper"
                            id={`${activeTab?.tabID}-content`}
                            aria-labelledby={`${activeTab?.tabID}-button`}
                            aria-expanded="true"
                            role="tabpanel"
                        >
                            {activeTab?.content}
                        </section>
                    </TabletLandscapeAndLarger>
                </>
            )}
        </>
    );
};

const MaterialTabContent: React.FC<ITabContentProps> = React.memo(
    ({
         tabKey,
         dimension,
         tabType,
         onHoverOption,
         onSelectOption,
         selectedDimension
     }: ITabContentProps) => {
        const {data} = useMarketSettings();

        const modalRef = useRef<IDynamicContentModalRef>(null);
        const lastActiveDimensionNodeRef = useRef<HTMLElement | null>(null);
        const translate = useTranslations();

        // Scrolls the active dimension list item into the view 24 pixels from the left of the screen.
        // This is only done once when a dimension is opened
        const activeRef = useCallback((node: HTMLLIElement) => {
            if (
                node !== null &&
                !node?.parentElement?.parentElement?.isEqualNode(
                    lastActiveDimensionNodeRef.current
                )
            ) {
                node?.parentElement?.parentElement?.scrollTo({
                    left: node.offsetLeft - 24, // 24 is the left margin value equal to $spacing-6 in the css
                    behavior: "smooth"
                });
                lastActiveDimensionNodeRef.current =
                    node?.parentElement?.parentElement ?? null;
            }
        }, []);

        const showBorder = useCallback((hexCode: string) => {
            const contrast = getContrast(hexCode, "#f5f5f5");
            return contrast > 2
                ? "0 transparent"
                : `1px solid ${darken(hexCode, 0.2)}`;
        }, []);

        const colors = useMemo(
            () =>
                dimension.options &&
                (dimension.options[tabKey]
                    .map(dimension => {
                        const colorFromSettings = data?.colors?.find(
                            color => color.colorId === dimension.id
                        );

                        if (!colorFromSettings) {
                            return;
                        }

                        return {
                            ...dimension,
                            ...colorFromSettings
                        };
                    })
                    .filter(Boolean) as DimensionAndColor[]),
            [dimension, tabKey, darken, data]
        );

        const materialLink = useMemo(
            () => (colors ? colors[0]?.materialLink : ""),
            [colors]
        );

        return (
            <>
                {tabType === "color-grid" && (
                    <div className="dimension-picker__color-grid">
                        {colors?.map(color => (
                            <MaterialTile
                                key={color.displayValue}
                                hexcode={color.hexCode ?? ""}
                                option={color}
                                onHoverOption={onHoverOption}
                                onSelectOption={onSelectOption}
                                isSelected={
                                    color.displayValue ===
                                    selectedDimension?.displayValue
                                }
                                isColorGrid={tabType === "color-grid"}
                            />
                        ))}
                    </div>
                )}
                {tabType === "color-regular" && (
                    <ul
                        className={cx("dimension-picker__list", {
                            "dimension-picker__list--mobile-center":
                                colors &&
                                colors.length < 5 &&
                                !dimension.isColour &&
                                dimension.dimensionName !== "leg finish",
                            "dimension-picker__list-colour--mobile-center":
                                dimension.isColour &&
                                colors &&
                                colors.length <= 12
                        })}
                    >
                        {colors?.map(color => (
                            <li
                                key={color.displayValue}
                                ref={
                                    selectedDimension?.displayValue ===
                                    color.displayValue
                                        ? activeRef
                                        : null
                                }
                            >
                                <MaterialTile
                                    hexcode={color.hexCode ?? ""}
                                    option={color}
                                    onHoverOption={onHoverOption}
                                    onSelectOption={onSelectOption}
                                    isSelected={
                                        color.displayValue ===
                                        selectedDimension?.displayValue
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                )}
                {tabType === "text-label" && (
                    <ul
                        className={cx(
                            "dimension-picker__list dimension-picker__list--text-label",
                            {
                                "dimension-picker__list--mobile-center":
                                    colors &&
                                    colors.length < 5 &&
                                    !dimension.isColour &&
                                    dimension.dimensionName !== "leg finish" &&
                                    dimension.dimensionName !==
                                    "upholstery type",
                                "dimension-picker__list-colour--mobile-center":
                                    dimension.isColour &&
                                    colors &&
                                    colors.length <= 12
                            }
                        )}
                    >
                        {colors?.map(color => (
                            <li
                                key={color.displayValue}
                                className={cx("dimension-picker__list-item", {
                                    "dimension-picker__list-item--active":
                                        selectedDimension?.displayValue ===
                                        color.displayValue &&
                                        !color?.colourSwatchImage
                                })}
                                ref={
                                    selectedDimension?.displayValue ===
                                    color.displayValue
                                        ? activeRef
                                        : null
                                }
                            >
                                {color?.colourSwatchImage ? (
                                    <button
                                        className={`dimension-picker__option${
                                            dimension.isColour
                                                ? "-color"
                                                : "-default"
                                        }`}
                                        onClick={() => onSelectOption(color)}
                                        onMouseOver={() =>
                                            onHoverOption(
                                                getDimensionTranslationValue(
                                                    translate,
                                                    color.translationDisplayValue
                                                )
                                            )
                                        }
                                        onMouseOut={() =>
                                            onHoverOption(undefined)
                                        }
                                    >
                                        <MaterialTile
                                            option={color}
                                            hexcode={color.hexCode ?? ""}
                                            isSelected={
                                                selectedDimension?.displayValue ===
                                                color.displayValue
                                            }
                                            onSelectOption={onSelectOption}
                                            onHoverOption={onHoverOption}
                                        />
                                        <div className="dimension-picker__option-default-text">
                                            {getDimensionTranslationValue(
                                                translate,
                                                color.translationDisplayValue
                                            )}
                                        </div>
                                    </button>
                                ) : (
                                    <button
                                        className={`dimension-picker__option${
                                            dimension.isColour
                                                ? "-color"
                                                : "-default"
                                        }`}
                                        onClick={(): void =>
                                            onSelectOption(color)
                                        }
                                    >
                                        {color.hexCode &&
                                            !color?.colourSwatchImage && (
                                                <div
                                                    className="dimension-picker__list-item-color-tile"
                                                    style={{
                                                        backgroundColor: `#${color?.hexCode}`,
                                                        border: showBorder(
                                                            `#${color?.hexCode}`
                                                        )
                                                    }}
                                                />
                                            )}
                                        <div className="dimension-picker__option-default-text">
                                            {getDimensionTranslationValue(
                                                translate,
                                                color.translationDisplayValue
                                            )}
                                        </div>
                                    </button>
                                )}
                            </li>
                        ))}
                        {!!dimension.options &&
                            dimension.options[tabKey].map(option => (
                                <li
                                    key={option.displayValue}
                                    className={cx(
                                        "dimensi on-picker__list-item",
                                        {
                                            "dimension-picker__list-item--active":
                                                selectedDimension?.displayValue ===
                                                option.displayValue
                                        }
                                    )}
                                    ref={
                                        selectedDimension?.displayValue ===
                                        option.displayValue
                                            ? activeRef
                                            : null
                                    }
                                >
                                    <button
                                        className={`dimension-picker__option${
                                            dimension.isColour
                                                ? "-color"
                                                : "-default"
                                        }`}
                                        onClick={(): void =>
                                            onSelectOption(option)
                                        }
                                    >
                                        <div className="dimension-picker__option-default-text">
                                            {getDimensionTranslationValue(
                                                translate,
                                                option.translationDisplayValue
                                            )}
                                        </div>
                                    </button>
                                </li>
                            ))}
                    </ul>
                )}
                {materialLink ? (
                    <>
                        <Button
                            style="text"
                            className="dimension-picker__material-link"
                            onClick={() => modalRef.current?.open()}
                        >
                            {translate(
                                t => t.productPage.materialsLinkText,
                                "Read more about"
                            )}{" "}
                            {tabKey}
                        </Button>
                        <DynamicContentModal
                            contentUrl={materialLink}
                            ref={modalRef}
                        />
                    </>
                ) : null}
            </>
        );
    }
);

const MaterialTile: React.FC<IMaterialTileProps> = ({
                                                        option,
                                                        hexcode,
                                                        onHoverOption,
                                                        onSelectOption,
                                                        isSelected,
                                                        isColorGrid
                                                    }) => {
    const [hover, setHover] = useState<boolean>(false);

    const handleMouseOver = useCallback(() => {
        setHover(true);
        onHoverOption(option.displayValue);
    }, [option]);

    const handleMouseOut = useCallback(() => {
        setHover(false);
        onHoverOption(undefined);
    }, []);

    return (
        <div
            style={{gridArea: `${option.seoFriendlyDisplayValue}`}}
            className={cx([
                "material-tile",
                {"material-tile--hover": hover && !isSelected},
                {"material-tile--color-grid": isColorGrid}
            ])}
        >
            <button
                style={{
                    background: !isColorGrid ? "#" + hexcode : "transparent"
                }}
                className={cx([
                    "material-tile__button",
                    {"material-tile__button--selected": isSelected}
                ])}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
                onClick={() => onSelectOption(option)}
            >
                {option.colourSwatchImage && (
                    <img
                        className="dimension-picker__option-color-box"
                        src={option.colourSwatchImage + "?width=76"}
                    />
                )}
                {isSelected && (
                    <div className="material-tile__selected-dot">
                        <SvgIcon iconName="checkmark"/>
                    </div>
                )}
            </button>
        </div>
    );
};

export const DimensionPicker: React.FC<IDimensionPicker> = React.memo(
    ({dimension}: IDimensionPicker): JSX.Element | null => {
        const {
            config,
            updateProductConfig,
            setProductImageUpdating
        } = useProduct();
        const [selectedDimension, setSelectedDimension] = useState<
            IProductConfigOption
        >();
        const [activeTab, setActiveTab] = useState<ITabMaterialProps>();
        const [tabsData, setTabsData] = useState<
            ITabMaterialProps[] | undefined
        >();
        const [hoverOptionValue, setHoverOptionValue] = useState<
            string | undefined
        >(undefined);

        const hasRoomForGrid = useMediaQuery({minWidth: 430});
        const isMobileLayout = useMediaQuery({maxWidth: 1024});

        const translate = useTranslations();

        if (!dimension) {
            return null;
        }

        const handleOptionSelect = (option: IOption): void => {
            updateProductConfig({
                dimensionName: dimension.dimensionName,
                seoFriendlyDimensionName: dimension.seoFriendlyDimensionName,
                translationDimensionName: dimension.translationDimensionName,
                condition: dimension.condition,
                invalidCondition: dimension.invalidCondition,
                isColour: dimension.isColour,
                ...option
            });

            setProductImageUpdating(true);

            if (isMobileLayout && isBrowser) {
                window.scrollTo({top: 0, behavior: "smooth"});
            }
        };

        const handleTabSelect = useCallback((tab: ITabMaterialProps): void => {
            setActiveTab(tab);
        }, []);

        const handleHoverOption = useCallback(
            (optionValue: string | undefined): void => {
                setHoverOptionValue(optionValue);
            },
            []
        );

        useEffect(() => {
            if (config?.dimensions) {
                setSelectedDimension(
                    config.dimensions.find(
                        d => d.dimensionName === dimension.dimensionName
                    )
                );
            }
        }, [config]);

        useEffect(() => {
            // We need to determine if we are to show a colorgrid instead of the normal dimension options layout.
            // Colour grid logic is applied if all dimension colors are of the main colors and that a minimum of 35 of the main colors are present.
            const tabTypes: IDimensionTabType[] = [];
            if (
                (dimension.isColour && !!dimension.options) ||
                (dimension.seoFriendlyDimensionName === "leg-finish" &&
                    !!dimension.options)
            ) {
                for (const [key, value] of Object.entries(dimension.options)) {
                    let onlyMainColors = true;
                    let mainColorCount = 0;

                    if (value.length) {
                        value
                            .map(o => {
                                const mappedOpt = {
                                    ...o,
                                    ...{
                                        seoFriendlyDisplayValue: o.seoFriendlyDisplayValue || o.id
                                    }
                                };
                                return mappedOpt;
                            })
                            .map(o => {
                                return mainColors.includes(
                                    o.id?.toLowerCase() || ''
                                );
                            })
                            .forEach(isMainColor => {
                                mainColorCount += isMainColor ? 1 : 0;
                                onlyMainColors = onlyMainColors && isMainColor;
                            });

                        if (
                            onlyMainColors &&
                            mainColorCount >= COLOR_GRID_TRESHOLD &&
                            hasRoomForGrid
                        ) {
                            tabTypes.push({
                                key,
                                type: "color-grid"
                            });
                        } else {
                            tabTypes.push({
                                key,
                                type: "color-regular"
                            });
                        }
                    }
                }
            } else {
                if (!!dimension.options) {
                    for (const [key] of Object.entries(dimension.options)) {
                        tabTypes.push({
                            key,
                            type: "text-label"
                        });
                    }
                }
            }

            const tabsWithData = tabTypes?.map(
                (tabItem): ITabMaterialProps => {
                    return {
                        tabName: tabItem.key,
                        tabID: tabItem.key,
                        tabType: tabItem.type,
                        content: (
                            <MaterialTabContent
                                tabType={tabItem.type}
                                tabKey={tabItem.key}
                                onHoverOption={handleHoverOption}
                                onSelectOption={handleOptionSelect}
                                selectedDimension={selectedDimension}
                                dimension={dimension}
                            />
                        )
                    };
                }
            );

            if (selectedDimension) {
                findActiveTab(tabsWithData);
            }

            setTabsData(tabsWithData);
        }, [selectedDimension]);

        const findActiveTab = (tabs: ITabMaterialProps[]) => {
            if (
                !!dimension.options &&
                Object.keys(dimension.options).length > 1
            ) {
                for (let x = 0, arrLen = tabs.length; x < arrLen; x++) {
                    // @ts-ignore
                    const hasTabSelectedColour = dimension.options?.[
                        tabs[x].tabID
                        ]
                        .map((opt: IOption) => opt.id)
                        .includes(selectedDimension?.colorId);

                    if (hasTabSelectedColour) {
                        setActiveTab(tabs[x]);
                        return;
                    }
                }
            }
            setActiveTab(tabs[0]);
        };

        const centerTabContent = (dimensionOptions: {
            [p: string]: IOption[];
        }): boolean => {
            for (const dimensionOptionElement in dimensionOptions) {
                if (dimensionOptions[dimensionOptionElement].length >= 14) {
                    return false;
                }
            }
            return true;
        };

        return (
            <>
                {!!dimension.options && (
                    <div
                        className={cx("dimension-picker", {
                            "dimension-picker--color": dimension.isColour,
                            "dimension-picker--color-center":
                            dimension.isColour,
                            "dimension-picker--color-center-content":
                                (dimension.isColour &&
                                    centerTabContent(dimension.options)) ||
                                activeTab?.tabType === "color-grid",
                            "dimension-picker--content":
                                !dimension.isColour &&
                                Object.keys(dimension.options).filter(
                                    k => k !== "default"
                                ).length > 0
                        })}
                    >
                        <div className="dimension-picker__wrapper">
                            <TabletLandscapeAndLarger>
                                <div className="dimension-picker__header">
                                    <span className="dimension-picker__header--emphasized">
                                        {dimension?.translationDimensionName}:
                                    </span>{" "}
                                    {getDimensionTranslationValue(
                                        translate,
                                        hoverOptionValue ??
                                        selectedDimension?.displayValue
                                    )}
                                </div>
                            </TabletLandscapeAndLarger>
                            <MaterialsTabList
                                tabsConfig={tabsData}
                                activeTab={activeTab}
                                onTabSelect={handleTabSelect}
                                selectedOption={getDimensionTranslationValue(
                                    translate,
                                    hoverOptionValue ??
                                    selectedDimension?.displayValue
                                )}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }
);
